import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Carrier Website Agent's screens. A run is one customer on one carrier's
 * website, filled by the Klaris Chrome extension in the person's own browser,
 * up to the point where a licensed person takes over.
 */

export type CwStatus = 'ready' | 'working' | 'waiting' | 'paused' | 'stuck' | 'stopped' | 'bound' | 'blocked' | 'ended';

export interface CwRunCard {
  id: string;
  carrier: string;
  carrier_name: string;
  customer: string | null;
  status: CwStatus;
  stage: string;
  mode: 'learn' | 'guide';
  screen: string | null;
  screens_done: number;
  price: number | null;
  price_kind: 'firm' | 'indication' | null;
  currency: 'USD' | 'INR';
  review: 'unsure' | 'every';
  reason: string | null;
  by: string | null;
  ai_calls: number;
  open_questions: number;
  created_at: string;
  finished_at: string | null;
}

export interface CwStep {
  id: string;
  kind: 'note' | 'fill' | 'click' | 'refused' | 'changed';
  screen: string | null;
  label: string | null;
  value: string | null;
  source: string | null;
  how: 'guide' | 'ai' | 'code' | 'person' | null;
  ok: boolean | null;
  note: string | null;
  field_key: string | null;
  at: string;
}

export interface CwQuestion {
  id: string;
  kind: 'fact' | 'code' | 'allow' | 'popup' | 'review';
  text: string;
  options: string[];
  answer: string | null;
  by: string | null;
  open: boolean;
  screen: string | null;
}

export interface CwRunDetail extends CwRunCard {
  thinking: boolean;
  steps: CwStep[];
  questions: CwQuestion[];
  shots: { id: string; screen: string | null; at: string }[];
  refused: number;
}

export interface CwSpotlight {
  runs: number;
  stopped: number;
  open: number;
  fields: number;
  checked: number;
  refused: number;
  guides: number;
}

export interface CwOptions {
  customers: {
    id: string; name: string; state: string | null; drivers: number; vehicles: number; current_carrier: string | null;
    source: string;
    source_detail: string | null;
  }[];
  carriers: { key: string; name: string; url: string; guide: 'draft' | 'approved' | null; version: number | null }[];
}

export interface CwGuideField {
  key: string;
  label: string | null;
  fact: string | null;
  ask: string | null;
  skip: boolean;
  format: string | null;
  /** What we put in it, in plain words: "Each driver's date of birth". */
  fills: string;
  /** How: the form it is written in, or what we ask. */
  how: string | null;
}

export interface CwGuideChange {
  id: string;
  why: string;
  status: 'proposed' | 'approved' | 'rejected';
  screen: string;
  field: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  at: string;
  by: string | null;
}

export interface CwGuide {
  carrier: string;
  name: string;
  status: 'draft' | 'approved' | null;
  version: number | null;
  approved_by: string | null;
  approved_at: string | null;
  screens: { key: string; title: string; repeats: string | null; next: string | null; fields: CwGuideField[] }[];
  changes: CwGuideChange[];
  proposed: number;
}

export interface CwStarted {
  run: CwRunCard;
  token: string;
  briefing: Record<string, unknown>;
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/carrier-website`;
}

async function call<T>(path: string, init: RequestInit = {}, fallback = 'Could not reach Klaris'): Promise<T> {
  const token = localStorage.getItem('klaris_jwt');
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || fallback);
  }
  return response.json();
}

const post = <T,>(path: string, body: unknown = {}) => call<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const fetchCwSpotlight = () => call<CwSpotlight>('/spotlight');
export const fetchCwOptions = () => call<CwOptions>('/start-options');
export const fetchCwRuns = () => call<{ runs: CwRunCard[] }>('/runs');
export const fetchCwRun = (id: string) => call<CwRunDetail>(`/runs/${id}`);
export const fetchCwGuides = () => call<{ guides: CwGuide[] }>('/guides');
export type CwReview = 'unsure' | 'every';

export const startCwRun = (customerId: string, carrier: string, review: CwReview = 'unsure') =>
  post<CwStarted>('/runs', { customer_id: customerId, carrier, review });
export const cwRunAction = (runId: string, action: 'pause' | 'resume' | 'end' | 'bound') => post(`/runs/${runId}/${action}`);
export const tryToBuy = (runId: string) => post<{ refused: boolean; why: string }>(`/runs/${runId}/try-to-buy`);
export const approveCwGuide = (carrier: string) => post(`/guides/${carrier}/approve`);
export const decideCwChange = (changeId: string, approve: boolean) => post(`/guide-changes/${changeId}`, { approve });

export async function fetchCwShot(shotId: string): Promise<string> {
  const token = localStorage.getItem('klaris_jwt');
  const response = await fetch(buildApiUrl(`${prefix()}/shots/${shotId}`), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error('Could not load the screenshot');
  return URL.createObjectURL(await response.blob());
}

/** The Klaris extension's version, if it is installed in this browser. */
export function extensionVersion(): string | null {
  return document.documentElement.dataset.klarisExtension ?? null;
}

/** Hands a new run to the extension, which opens the carrier's website. */
export function handToExtension(started: CwStarted): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', listen);
      reject(new Error('The Klaris extension did not answer. Reload this page and try again.'));
    }, 8000);
    function listen(event: MessageEvent) {
      if (event.source !== window || event.data?.source !== 'klaris-extension' || event.data.type !== 'started') return;
      window.clearTimeout(timer);
      window.removeEventListener('message', listen);
      if (event.data.ok) resolve();
      else reject(new Error(event.data.error || 'The Klaris extension could not open the website'));
    }
    window.addEventListener('message', listen);
    window.postMessage({
      source: 'klaris-page',
      type: 'start-run',
      run: { id: started.run.id, token: started.token, api: new URL(buildApiUrl('/'), window.location.href).origin, briefing: started.briefing },
    }, window.location.origin);
  });
}
